// Berechtigungslogik für das modulare Lizenzmodell (reine Funktionen, kein React).
// Arbeitet auf dem rohen User-Dokument (users/{uid}) — alle Felder optional,
// Bestandsuser ohne Lizenzfelder funktionieren über das Legacy-enabledFeatures-Array
// unverändert weiter (Dual-Read).
// Backend-Spiegelung: functions/shared/moduleAccess.js — synchron halten!

import { MODULES, PLANS, ALL_MODULE_KEYS, BASE_FEATURES, LAB_FEATURES, GRACE_PERIOD_DAYS } from './moduleRegistry';

const GRACE_MS = GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000;

// Firestore Timestamp / Date / ISO-String / Millis → Millisekunden (oder null).
export function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function getUserPlan(userData) {
  return PLANS[userData?.licensePlan] || null;
}

// Abwahl wirkt erst zum Periodenende: bis removalEffectiveAt bleibt das Modul aktiv.
function isRemovalEffective(userData, moduleKey, now) {
  const effectiveAt = toMillis(userData?.moduleRemovals?.[moduleKey]);
  return effectiveAt != null && effectiveAt <= now;
}

export function isTrialActive(userData, moduleKey, now = Date.now()) {
  const endsAt = toMillis(userData?.moduleTrials?.[moduleKey]?.endsAt);
  return endsAt != null && endsAt > now;
}

export function hasUsedTrial(userData, moduleKey) {
  return !!userData?.moduleTrials?.[moduleKey];
}

// ── Billing (Paddle) ─────────────────────────────────────────────────

// Karenz nach fehlgeschlagener Zahlung: past_due gewährt Zugriff bis
// currentPeriodEnd + Karenz. Ohne bekanntes Periodenende fail-open —
// eine Sperre erfolgt nie allein wegen fehlender Daten.
export function isInGracePeriod(userData, now = Date.now()) {
  if (userData?.billingStatus !== 'past_due') return false;
  const periodEnd = toMillis(userData?.currentPeriodEnd);
  if (periodEnd == null) return true;
  return now <= periodEnd + GRACE_MS;
}

// Zentrales Billing-Gate für Plan- und gekaufte Module. true wenn:
// kein Billing-Status (Legacy/Telegram/Admin ohne Paddle), manualOverride,
// active, past_due innerhalb der Karenz, oder cancelled mit Restlaufzeit.
// paused/expired (und abgelaufene Restlaufzeit) ⇒ Module „Pausiert".
// Tester, Trials und Legacy-enabledFeatures bleiben davon unberührt.
export function isLicenseBillingActive(userData, now = Date.now()) {
  const status = userData?.billingStatus;
  if (!status) return true;
  if (userData?.billing?.manualOverride) return true;
  if (status === 'active') return true;
  if (status === 'past_due') return isInGracePeriod(userData, now);
  if (status === 'cancelled') {
    const periodEnd = toMillis(userData?.currentPeriodEnd);
    return periodEnd != null && periodEnd > now;
  }
  return false;
}

// Existiert eine Paddle-Subscription, die aktuell Zugriff gewährt?
export function hasActiveSubscription(userData, now = Date.now()) {
  return !!userData?.billing?.paddleSubscriptionId && isLicenseBillingActive(userData, now);
}

// Aktive Module über das Lizenzmodell (OHNE Legacy-Ableitung).
export function getActiveModules(userData, now = Date.now()) {
  if (!userData) return [];

  if (userData.isTester) {
    const restricted = Array.isArray(userData.testerModules) && userData.testerModules.length > 0;
    return restricted
      ? userData.testerModules.filter((k) => MODULES[k])
      : [...ALL_MODULE_KEYS];
  }

  const active = new Set();
  const plan   = getUserPlan(userData);

  // Plan- und gekaufte Module zählen nur bei aktivem Billing (bzw. ohne
  // Billing-Status: Legacy/Telegram/Admin). Trials bleiben unberührt.
  if (plan && isLicenseBillingActive(userData, now)) {
    (plan.includedModules || plan.baseModules || []).forEach((k) => active.add(k));
    (userData.selectedModules || []).forEach((k) => {
      if (!isRemovalEffective(userData, k, now)) active.add(k);
    });
    (userData.purchasedModules || []).forEach((k) => {
      if (!isRemovalEffective(userData, k, now)) active.add(k);
    });
  }

  Object.keys(userData.moduleTrials || {}).forEach((k) => {
    if (isTrialActive(userData, k, now)) active.add(k);
  });

  return [...active].filter((k) => MODULES[k]);
}

// Legacy-Ableitung: Ein Modul gilt als "legacy-aktiv", wenn eines seiner
// Features im alten enabledFeatures-Array freigeschaltet ist (Rollout-Fallback).
export function isModuleLegacyActive(userData, moduleKey) {
  const mod = MODULES[moduleKey];
  if (!mod || mod.includedFeatures.length === 0) return false;
  const legacy = userData?.enabledFeatures || [];
  return mod.includedFeatures.some((f) => legacy.includes(f));
}

// Zentrale Modul-Prüfung (inkl. Legacy-Fallback für Bestandsuser).
export function hasModule(userData, moduleKey, now = Date.now()) {
  if (getActiveModules(userData, now).includes(moduleKey)) return true;
  return isModuleLegacyActive(userData, moduleKey);
}

// Dual-Read: Feature aktiv über Legacy-Array ODER über ein aktives Modul.
// Grundausstattungs-Features sind für jeden Nutzer immer aktiv.
// Labor-Features hängen ausschließlich am Opt-in (Feature-Labor).
export function hasFeatureAccess(userData, featureId, now = Date.now()) {
  if (BASE_FEATURES.includes(featureId)) return true;
  if (LAB_FEATURES.includes(featureId)) {
    return userData?.userSettings?.advanced?.labFeaturesEnabled === true;
  }
  if ((userData?.enabledFeatures || []).includes(featureId)) return true;
  const activeModules = getActiveModules(userData, now);
  return activeModules.some((k) => MODULES[k].includedFeatures.includes(featureId));
}

// Anzahl der belegten Wahlmodul-Slots (geplante Abwahl belegt den Slot
// bis zum Wirksamkeitsdatum weiter; Trials belegen KEINEN Slot).
export function getUsedModuleSlots(userData, now = Date.now()) {
  const plan = getUserPlan(userData);
  if (!plan || plan.selectableModuleLimit == null) return 0;
  const base = new Set(plan.baseModules || []);
  return (userData.selectedModules || []).filter(
    (k) => MODULES[k] && !base.has(k) && !isRemovalEffective(userData, k, now)
  ).length;
}

export function getRemainingModuleSlots(userData, now = Date.now()) {
  const plan = getUserPlan(userData);
  if (!plan || plan.selectableModuleLimit == null) return 0;
  return Math.max(0, plan.selectableModuleLimit - getUsedModuleSlots(userData, now));
}

export function canSelectMoreModules(userData, now = Date.now()) {
  return getRemainingModuleSlots(userData, now) > 0;
}

// Module ohne Zugriff (weder aktiv noch legacy-aktiv).
export function getLockedModules(userData, now = Date.now()) {
  return ALL_MODULE_KEYS.filter((k) => !hasModule(userData, k, now));
}

// Für Upgrade-Anzeige: gesperrte Module, die real verfügbar sind (kein "Bald verfügbar").
export function getAvailableModulesForUpgrade(userData, now = Date.now()) {
  return getLockedModules(userData, now).filter((k) => MODULES[k].status === 'available');
}

// Effektives Credit-Limit: explizites Feld → Tarif → Tester-Default (Business).
export function getEffectiveCreditLimit(userData) {
  if (userData?.creditLimit !== undefined && userData?.creditLimit !== null) return userData.creditLimit;
  if (userData?.isTester) return PLANS.business.creditLimit;
  const plan = getUserPlan(userData);
  return plan ? plan.creditLimit : null;
}

// UI-Status einer Modulkarte. Rückgabe: { status, trialEndsAt?, removalEffectiveAt? }
// status: included | active | trial | paused | available | upgrade_required | coming_soon | legacy
export function getModuleStatus(userData, moduleKey, now = Date.now()) {
  const mod  = MODULES[moduleKey];
  if (!mod) return { status: 'unknown' };
  const plan = getUserPlan(userData);

  if (userData?.isTester && getActiveModules(userData, now).includes(moduleKey)) {
    return { status: 'included' };
  }
  if (isTrialActive(userData, moduleKey, now)) {
    return { status: 'trial', trialEndsAt: toMillis(userData.moduleTrials[moduleKey].endsAt) };
  }
  if (plan && isLicenseBillingActive(userData, now) &&
      (plan.includedModules || plan.baseModules || []).includes(moduleKey)) {
    return { status: 'included' };
  }
  if (getActiveModules(userData, now).includes(moduleKey)) {
    const removalAt = toMillis(userData?.moduleRemovals?.[moduleKey]);
    return removalAt && removalAt > now
      ? { status: 'active', removalEffectiveAt: removalAt }
      : { status: 'active' };
  }
  if (!plan && !userData?.isTester && isModuleLegacyActive(userData, moduleKey)) {
    return { status: 'legacy' }; // Bestandsuser ohne Plan: "Enthalten" ohne Billing-Infos
  }
  // Pausiert: schon einmal aktiv gewesen, Testphase abgelaufen ODER Tarif-Modul
  // mit inaktivem Billing (nur dann erreicht dieser Code Tarif-Module) — nie "gesperrt".
  const isPlanModule = !!plan && (plan.includedModules || plan.baseModules || []).includes(moduleKey);
  if (isPlanModule ||
      (userData?.previouslyActiveModules || []).includes(moduleKey) ||
      hasUsedTrial(userData, moduleKey)) {
    return { status: 'paused' };
  }
  if (mod.status === 'coming_soon') return { status: 'coming_soon' };
  return canSelectMoreModules(userData, now)
    ? { status: 'available' }
    : { status: 'upgrade_required' };
}
